import { Head, Link, router } from '@inertiajs/react'
import { Plus, Search, X } from 'lucide-react'
import { useEffect, useMemo, useState, type ReactNode } from 'react'

import { ColumnHeader } from '@/components/ColumnHeader'
import { PageHeader } from '@/components/PageHeader'
import { Pagination } from '@/components/Pagination'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { DashboardLayout } from '@/layouts/DashboardLayout'
import { founderPath, foundersPath, newFounderPath } from '@/lib/routes'
import { useRememberThisUrl } from '@/lib/rememberedUrl'
import { useDebouncedCallback } from '@/lib/useDebouncedCallback'
import type { PaginationMeta } from '@/types/inertia'

type Country = { code: string; name: string; flag: string }

type CompanyTag = {
  id: number
  name: string
  slug: string | null
  role: string | null
  active: boolean
}

type FounderRow = {
  id: number
  slug: string
  name: string
  email: string | null
  avatar_url: string | null
  country: Country | null
  companies: CompanyTag[]
}

type FilterState = {
  name: string | null
  country: string[]
  company_id: string[]
  has_kalio: string | null
}

type SortState = {
  column: string
  direction: 'asc' | 'desc'
}

type LabeledOption = { value: string; label: string }

type FilterOptions = {
  countries: LabeledOption[]
  companies: LabeledOption[]
}

type FoundersIndexProps = {
  founders: FounderRow[]
  pagination: PaginationMeta
  filters: FilterState
  sort: SortState
  filter_options: FilterOptions
}

type QueryParams = Record<string, string | string[] | null>

function buildQuery(filters: FilterState, sort: SortState): QueryParams {
  const sortParam = sort.direction === 'desc' ? `-${sort.column}` : sort.column
  return {
    name: filters.name || null,
    country: filters.country.length ? filters.country : null,
    company_id: filters.company_id.length ? filters.company_id : null,
    has_kalio: filters.has_kalio || null,
    sort: sortParam === 'name' ? null : sortParam,
  }
}

const PARTIAL_RELOAD_KEYS = ['founders', 'pagination', 'filters', 'sort']

function visitWith(query: QueryParams) {
  const clean: Record<string, string | string[]> = {}
  Object.entries(query).forEach(([k, v]) => {
    if (v !== null) clean[k] = v
  })
  router.get(foundersPath(), clean, {
    preserveState: true,
    preserveScroll: true,
    replace: true,
    only: PARTIAL_RELOAD_KEYS,
  })
}

function Chip({ label, value, onRemove }: { label: string; value: string; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted px-3 py-1 text-xs">
      <span className="text-muted-foreground">{label}:</span>
      <span className="font-medium text-foreground">{value}</span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${label} ${value}`}
        className="flex h-4 w-4 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-background hover:text-foreground"
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  )
}

function FoundersIndex({
  founders,
  pagination,
  filters,
  sort,
  filter_options: filterOptions,
}: FoundersIndexProps) {
  useRememberThisUrl('founders')

  const [nameInput, setNameInput] = useState(filters.name ?? '')

  useEffect(() => {
    setNameInput(filters.name ?? '')
  }, [filters.name])

  const searchDebounced = useDebouncedCallback((nextName: string) => {
    visitWith(buildQuery({ ...filters, name: nextName || null }, sort))
  }, 300)

  const onNameChange = (value: string) => {
    setNameInput(value)
    searchDebounced(value)
  }

  const clearName = () => {
    setNameInput('')
    searchDebounced.cancel()
    visitWith(buildQuery({ ...filters, name: null }, sort))
  }

  const setFilter = <K extends keyof FilterState>(key: K, value: FilterState[K]) => {
    visitWith(buildQuery({ ...filters, [key]: value }, sort))
  }

  const setSort = (column: string, direction: 'asc' | 'desc') => {
    visitWith(buildQuery(filters, { column, direction }))
  }

  const clearAll = () => {
    setNameInput('')
    searchDebounced.cancel()
    visitWith({})
  }

  const hasFilters = useMemo(() => {
    return (
      Boolean(filters.name) ||
      filters.country.length > 0 ||
      filters.company_id.length > 0 ||
      Boolean(filters.has_kalio)
    )
  }, [filters])

  const paginationQuery = buildQuery(filters, sort)

  const countryOptions = filterOptions.countries
  const companyOptions = filterOptions.companies
  const companyLabelById = useMemo(
    () => new Map(filterOptions.companies.map((c) => [c.value, c.label])),
    [filterOptions.companies],
  )

  return (
    <>
      <Head title="Founders" />

      <div className="mx-auto max-w-6xl px-8 py-10">
        <PageHeader
          title="Founders"
          description="Founders and operators — the humans behind the portfolio."
          actions={
            <Link href={newFounderPath()}>
              <Button>
                <Plus />
                New founder
              </Button>
            </Link>
          }
        />

        <div className="relative mb-4">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="h-12 pl-11 pr-12 text-base"
            placeholder="Search founders by name — accents and case are ignored…"
            value={nameInput}
            onChange={(e) => onNameChange(e.target.value)}
          />
          {nameInput && (
            <button
              type="button"
              onClick={clearName}
              aria-label="Clear search"
              className="absolute right-3 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {hasFilters && (
          <div className="mb-3 flex flex-wrap items-center gap-2">
            {filters.country.map((v) => {
              const opt = countryOptions.find((o) => o.value === v)
              return (
                <Chip
                  key={`c-${v}`}
                  label="Country"
                  value={opt?.label ?? v}
                  onRemove={() => setFilter('country', filters.country.filter((x) => x !== v))}
                />
              )
            })}
            {filters.company_id.map((v) => (
              <Chip
                key={`co-${v}`}
                label="Company"
                value={companyLabelById.get(v) ?? v}
                onRemove={() =>
                  setFilter('company_id', filters.company_id.filter((x) => x !== v))
                }
              />
            ))}
            {filters.has_kalio && (
              <Chip
                label="Kalio"
                value="Linked only"
                onRemove={() => setFilter('has_kalio', null)}
              />
            )}
            <button
              type="button"
              onClick={clearAll}
              className="text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
            >
              Clear all
            </button>
          </div>
        )}

        <div className="mb-3 flex items-center justify-end text-xs text-muted-foreground">
          <span className="tabular-nums">{pagination.total_count} total</span>
        </div>

        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="px-6 py-3 text-left">
                    <ColumnHeader column="name" label="Name" sort={sort} onSort={setSort} />
                  </th>
                  <th className="px-6 py-3 text-left">
                    <ColumnHeader
                      column="companies"
                      label="Companies"
                      sort={sort}
                      onSort={setSort}
                      filterLabel="Company"
                      filterOptions={companyOptions}
                      filterValue={filters.company_id}
                      onFilterChange={(v) => setFilter('company_id', v)}
                    />
                  </th>
                  <th className="px-6 py-3 text-left">
                    <ColumnHeader
                      column="country"
                      label="Country"
                      sort={sort}
                      onSort={setSort}
                      filterLabel="Country"
                      filterOptions={countryOptions}
                      filterValue={filters.country}
                      onFilterChange={(v) => setFilter('country', v)}
                    />
                  </th>
                  <th className="px-6 py-3 text-left">
                    <ColumnHeader column="email" label="Email" sort={sort} onSort={setSort} />
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {founders.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-6 py-16 text-center">
                      <p className="text-sm text-muted-foreground">
                        {hasFilters ? 'No founders match these filters.' : 'No founders yet.'}
                      </p>
                    </td>
                  </tr>
                )}
                {founders.map((p) => (
                  <tr
                    key={p.id}
                    className="cursor-pointer transition-colors hover:bg-muted/50"
                    onClick={() => router.visit(founderPath(p.slug))}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full border border-border bg-muted">
                          {p.avatar_url ? (
                            <img
                              src={p.avatar_url}
                              alt=""
                              loading="lazy"
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <span className="text-[10px] text-muted-foreground">
                              {p.name.slice(0, 1).toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium">{p.name}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-muted-foreground">
                      {p.companies.length === 0 ? (
                        '—'
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {p.companies.map((c) => (
                            <span
                              key={`${p.id}-${c.id}-${c.role ?? ''}`}
                              className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2 py-0.5 text-xs text-foreground"
                              title={c.role ?? undefined}
                            >
                              <span>{c.name}</span>
                              {c.role && (
                                <span className="text-muted-foreground">· {c.role}</span>
                              )}
                              {!c.active && (
                                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                                  past
                                </span>
                              )}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-muted-foreground">
                      {p.country ? (
                        <span className="inline-flex items-center gap-1.5">
                          <span>{p.country.flag}</span>
                          <span>{p.country.name}</span>
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-6 py-4 text-muted-foreground">{p.email ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <div className="mt-4">
          <Pagination
            basePath={foundersPath()}
            currentPage={pagination.current_page}
            nextPage={pagination.next_page}
            prevPage={pagination.prev_page}
            totalPages={pagination.total_pages}
            query={paginationQuery as Record<string, string | number | string[] | null | undefined>}
          />
        </div>
      </div>
    </>
  )
}

export default Object.assign(FoundersIndex, {
  layout: (page: ReactNode) => <DashboardLayout>{page}</DashboardLayout>,
})
